import { useId, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode, type RefObject } from 'react'

import { cx, NARROW_QUERY, useMediaQuery } from './hooks'

/*
 * The pieces every point-series chart here shares (`TrendChart`, `DualSeriesChart` and
 * the Revenue page's charts): a cursor moved by the pointer or the arrow keys whose
 * readout is announced politely, the horizontal grid with its value labels, and the
 * visually hidden table that carries the whole series for a screen reader. `ChartFrame`
 * and `useChartBox` hold them together for the Revenue page's refund timeline and
 * lifecycle chart. Their classes are the trend chart's (`ci-trend__*`), the one chart
 * vocabulary in `styles.css`.
 */

export interface ChartCursor {
  /** The point under the cursor, or null when there is none. */
  active: number | null
  readoutId: string
  /** Spread onto the focusable plot wrapper: keyboard moves and blur. */
  plotProps: {
    role: 'group'
    tabIndex: 0
    'aria-describedby': string
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => void
    onBlur: () => void
  }
  /** Spread onto the `<svg>`: pointer moves and leave. */
  svgProps: {
    ref: RefObject<SVGSVGElement | null>
    onPointerMove: (event: PointerEvent<SVGSVGElement>) => void
    onPointerLeave: () => void
  }
}

/**
 * The cursor over `count` points. `indexAt` turns an x position in viewBox units
 * (`width` wide) into a point index; it is clamped to the series here. Arrow keys
 * move one point, Home and End jump, and the first key press starts at the last point.
 */
export function useChartCursor(count: number, width: number, indexAt: (x: number) => number): ChartCursor {
  const [active, setActive] = useState<number | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const readoutId = useId()
  const last = count - 1
  const clamp = (index: number) => Math.max(0, Math.min(last, index))

  const onPointerMove = (event: PointerEvent<SVGSVGElement>) => {
    const rect = svgRef.current?.getBoundingClientRect()
    if (!rect || rect.width === 0) return
    setActive(clamp(indexAt(((event.clientX - rect.left) / rect.width) * width)))
  }

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const moves: Record<string, (index: number) => number> = {
      ArrowRight: (index) => Math.min(last, index + 1),
      ArrowLeft: (index) => Math.max(0, index - 1),
      Home: () => 0,
      End: () => last,
    }
    const move = moves[event.key]
    if (!move) return
    event.preventDefault()
    setActive((index) => move(index ?? last))
  }

  return {
    active,
    readoutId,
    plotProps: { role: 'group', tabIndex: 0, 'aria-describedby': readoutId, onKeyDown, onBlur: () => setActive(null) },
    svgProps: { ref: svgRef, onPointerMove, onPointerLeave: () => setActive(null) },
  }
}

/** The horizontal grid lines from `min` (0 unless given: a series that goes below 0) to `max`, each labelled at the left edge. */
export function ChartYGrid({
  min = 0,
  max,
  ticks,
  left,
  right,
  y,
  label,
}: {
  min?: number
  max: number
  ticks: number
  left: number
  right: number
  y: (value: number) => number
  label: (value: number) => string
}) {
  return (
    <>
      {Array.from({ length: ticks + 1 }, (_, tick) => {
        const value = min + ((max - min) / ticks) * tick
        return (
          <g key={tick} className="ci-trend__grid">
            <line x1={left} x2={right} y1={y(value)} y2={y(value)} />
            <text x={left - 8} y={y(value) + 4} textAnchor="end">
              {label(value)}
            </text>
          </g>
        )
      })}
    </>
  )
}

/**
 * The whole series as a table a screen reader can walk, hidden from view. Hidden via a
 * wrapper: a table sizes to its content and ignores the 1px width.
 */
export function ChartDataTable({
  title,
  columns,
  rows,
}: {
  title: string
  /** The header row: the point's column first. */
  columns: readonly string[]
  /** One row per point: its name, then a cell per remaining column. */
  rows: readonly { key: string; name: ReactNode; cells: readonly ReactNode[] }[]
}) {
  return (
    <div className="ci-visually-hidden">
      <table>
        <caption>{title}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column} scope="col">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <th scope="row">{row.name}</th>
              {row.cells.map((cell, index) => (
                <td key={index}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** The viewBox widths a framed chart draws at: narrower on a phone, so its axis text stays legible when the chart is scaled down. */
export const CHART_WIDE = 640
export const CHART_NARROW = 360

export interface ChartPad {
  left: number
  right: number
  top: number
  bottom: number
}

/** A chart's viewBox and the plot inside its padding, in viewBox units. */
export interface ChartBox {
  width: number
  height: number
  innerWidth: number
  innerHeight: number
  /** The plot's edges. */
  left: number
  right: number
  top: number
  bottom: number
}

/** The box a chart of `height` draws in at this screen's width (`CHART_WIDE`, or `CHART_NARROW` on a phone). */
export function useChartBox(height: number, pad: ChartPad): ChartBox {
  const width = useMediaQuery(NARROW_QUERY) ? CHART_NARROW : CHART_WIDE
  return {
    width,
    height,
    innerWidth: width - pad.left - pad.right,
    innerHeight: height - pad.top - pad.bottom,
    left: pad.left,
    right: width - pad.right,
    top: pad.top,
    bottom: height - pad.bottom,
  }
}

/** Label every `labelStep(count, most)`th of `count` points, so no more than `most` labels crowd the axis. */
export function labelStep(count: number, most: number): number {
  return Math.max(1, Math.ceil(count / most))
}

/**
 * A chart's frame: the focusable plot (the cursor's keyboard and pointer handling) around
 * the SVG the chart draws, the readout announced as the cursor moves, the legend, and the
 * visually hidden table. A chart draws only its marks (`children`) and says what its
 * readout, legend and table hold.
 */
export function ChartFrame({
  title,
  hint = 'read values',
  box,
  cursor,
  readout,
  legend,
  table,
  className,
  legendClassName,
  children,
}: {
  /** What the chart shows: the plot's accessible name. */
  title: string
  /** What the arrow keys step through, for the plot's name: "read each day". */
  hint?: string
  box: ChartBox
  cursor: ChartCursor
  /**
   * The point under the cursor: its readout's lines, and where they sit (in viewBox units
   * across); null when there is none. One value, so the two can never disagree.
   */
  readout: { at: number; content: ReactNode } | null
  legend: ReactNode
  table: ReactNode
  className?: string
  legendClassName?: string
  /** The chart's marks, drawn in the frame's SVG (viewBox `box`). */
  children: ReactNode
}) {
  return (
    <figure className={cx('ci-trend', className)}>
      <div className="ci-trend__plot" aria-label={`${title}. Use the arrow keys to ${hint}.`} {...cursor.plotProps}>
        <svg viewBox={`0 0 ${box.width} ${box.height}`} className="ci-trend__svg" aria-hidden="true" focusable="false" {...cursor.svgProps}>
          {children}
        </svg>
        <div
          id={cursor.readoutId}
          className="ci-trend__readout"
          aria-live="polite"
          data-visible={readout ? 'true' : 'false'}
          style={readout ? { left: `${(readout.at / box.width) * 100}%` } : undefined}
        >
          {readout?.content}
        </div>
      </div>
      <figcaption className={cx('ci-trend__legend', legendClassName)}>{legend}</figcaption>
      {table}
    </figure>
  )
}
